import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { Button } from '@heidi/design-system/button';
import { INHERIT_TYPE_SIZE_CLASS } from '@heidi/design-system/styles';
import { cn, getInitials } from '@heidi/utils';
import {
  AudioLines,
  Blocks,
  File,
  FileText,
  FolderOpen,
  Globe,
  ListTodo,
  Mail,
  MessageSquare,
  UserRound,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import type { RefKind } from '../../contracts';

/** The glyph each object kind is drawn with, wherever it has no avatar. */
export const OBJECT_KIND_ICON: Record<RefKind, LucideIcon> = {
  patient: UserRound,
  session: AudioLines,
  document: FileText,
  template: Blocks,
  routine: Workflow,
  project: MessageSquare,
  task: ListTodo,
  letter: Mail,
  file: File,
  collection: FolderOpen,
  source: Globe,
};

const OBJECT_TOKEN_CLASS = cn(
  'group/object-token relative isolate mx-0.5 inline-flex h-auto max-w-none shrink-0 gap-1 whitespace-nowrap rounded-full bg-transparent p-0 align-baseline font-normal text-text-secondary transition-colors duration-150 hover:bg-transparent hover:text-text-primary active:bg-transparent aria-expanded:bg-transparent aria-expanded:text-text-secondary',
  INHERIT_TYPE_SIZE_CLASS
);

type ChatTokenChipProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children' | 'disabled'
> & {
  readonly label: string;
  readonly kindNoun?: string;
  readonly mark?: ReactNode;
  readonly selected?: boolean;
  readonly disabled?: boolean;
  /** Bounded token kind for tests/styling; never an id or a name. */
  readonly dataKind?: string;
};

/**
 * The shared visual of an inline chat token, with no model behind it: it reads
 * as part of the sentence and is a real button only when it has an `onClick`.
 * The live DOM carries only the bounded `kind`: PostHog replay masks text but
 * records attribute values verbatim, so no id, name or `title`.
 */
export const ChatTokenChip = forwardRef<HTMLButtonElement, ChatTokenChipProps>(
  function ChatTokenChip(
    {
      label,
      kindNoun,
      mark,
      selected = false,
      disabled = false,
      dataKind,
      onClick,
      className,
      ...rest
    },
    ref
  ) {
    const interactive = onClick !== undefined && !disabled;
    const content = (
      <>
        {disabled ? null : (
          <span
            aria-hidden="true"
            data-testid="chat-object-token-fill"
            className={cn(
              'pointer-events-none absolute -inset-x-1 -inset-y-0.5 -z-10 rounded-full bg-transparent',
              interactive &&
                'group-hover/object-token:bg-background-tertiary [[data-user-message]_&]:group-hover/object-token:bg-background-secondary-hover',
              selected && 'bg-background-tertiary'
            )}
          />
        )}
        {kindNoun ? <span className="sr-only">{kindNoun}</span> : null}
        {mark}
        <span className="min-w-0 self-baseline truncate">{label}</span>
      </>
    );

    return (
      <Button
        ref={ref}
        asChild={!interactive}
        type={interactive ? 'button' : undefined}
        variant="ghost"
        size="xs"
        onClick={interactive ? onClick : undefined}
        data-testid="chat-object-token"
        data-object-kind={dataKind}
        aria-disabled={disabled || undefined}
        className={cn(
          OBJECT_TOKEN_CLASS,
          !interactive && 'cursor-default',
          !interactive && !selected && 'hover:text-text-secondary',
          selected && !disabled && 'text-text-primary',
          disabled && 'pointer-events-none text-text-disabled',
          className
        )}
        {...rest}
      >
        {interactive ? content : <span>{content}</span>}
      </Button>
    );
  }
);

/** The patient-identity avatar shared by a token and its preview card. */
export function PatientAvatar({
  name,
  avatarSeed,
}: {
  readonly name: string;
  readonly avatarSeed?: string;
}): JSX.Element {
  return (
    <Avatar
      size="xs"
      color={getAvatarColor(avatarSeed ?? name)}
      aria-hidden="true"
      className="shrink-0"
    >
      <AvatarFallback>{getInitials(name)}</AvatarFallback>
    </Avatar>
  );
}

/**
 * The glyph that precedes the label: a patient is their avatar; a session is
 * its patient's avatar when it has one and the recording mark when it does
 * not. Other kinds carry no glyph — their name is the whole token.
 */
export function TokenMark({
  kind,
  label,
  patientName,
  avatarSeed,
}: {
  readonly kind: string;
  readonly label: string;
  readonly patientName?: string;
  readonly avatarSeed?: string;
}): ReactNode {
  const avatarName =
    kind === 'patient'
      ? (patientName ?? label)
      : kind === 'session'
        ? patientName
        : undefined;

  if (avatarName) {
    return <PatientAvatar name={avatarName} avatarSeed={avatarSeed} />;
  }

  if (kind !== 'session') return null;
  const SessionIcon = OBJECT_KIND_ICON.session;
  return <SessionIcon aria-hidden="true" className="size-4 shrink-0" />;
}
